import { browserProfilingIntegration, browserTracingIntegration, init } from '@sentry/react';
import { isProd } from 'config/env';
import { getNileAppBuild } from 'utils/nile-app';

type SentryInitOptions = Parameters<typeof init>[0];
type SentryBeforeSend = NonNullable<SentryInitOptions['beforeSend']>;
type SentryBeforeSendEvent = Parameters<SentryBeforeSend>[0];

export const SENTRY_IGNORE_ERRORS: SentryInitOptions['ignoreErrors'] = [
  // Generic Noise & Promise Rejections
  /^Non-Error exception captured/i,
  /^Non-Error promise rejection captured/i,

  // Network & Module Loading (Regex for flexibility)
  /Failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /Importing a module script failed/i,
  /Load failed/i,
  /Unable to preload CSS for /i, // Vite: stale hashed CSS after deploy; handled by reload in Screen/index
  /TypeError: 'text\/(html|plain)' is not a valid JavaScript MIME type/i, // Stale chunk after deploy (404 body served as text/html or text/plain)
  /Unexpected token '<'/i, // Often happens when a script 404s and returns HTML
  /InvalidStateError: Failed to update a ServiceWorker/i, // Service worker in invalid state (stale registration, script 'Unknown', etc.)
  // WebKit throws this from any API called on a document whose script execution context has been
  // stopped: the tab was closed, navigated away, or suspended into the back-forward cache while an
  // async callback was still pending. The page is gone by the time it throws, so no caller can act
  // on it and no retry is possible. Safari's DOMException carries no `stack`, so Sentry reports it
  // stackless as `Error: InvalidStateError: <message>` with a `DOMException.code: 11` tag, which is
  // why this matches on the message rather than a call site.
  /Script execution context has stopped/i,
  // WebKit's AbortError form of the same teardown: a pending DOM promise is rejected as the page
  // unloads, e.g. the reload that follows a profile switch (MARKET-2M3). Equally stackless.
  /Promise was rejected because the browsing context is going away/i,
  /ServiceWorker script at .+ encountered an error during installation/i, // Transient SW install failure (mid-deploy / network)

  // Firebase Specific (Auth/Network)
  /SignOutError: Firebase: Error \(auth\/user-token-expired\)/i,
  'Firebase: Error (auth/network-request-failed)',
  /messaging\/token-subscribe-failed/i, // Transient network failure reaching fcmregistrations.googleapis.com; already handled by useSyncPushToken
  // Firebase Installations rejects before registering a new device while `navigator.onLine` is
  // false. Analytics starts that registration from getAnalytics() and stores the init promise
  // without a catch handler, so the rejection escapes as unhandled. It retries itself once the
  // browser is back online and no analytics call of ours is lost, so there is nothing to act on.
  /installations\/app-offline/i,
  /auth\/user-cancelled/i, // User closed / denied IdP consent; message text varies by Firebase version
  // Firebase rejects a redirect result whose session does not match the sign-in that started it
  // (a stale or replayed redirect). useAuthProvider tells the user to sign in again (MARKET-1YH).
  /auth\/invalid-session-id/i,
  'Firebase: Error (auth/admin-restricted-operation)',
  'Firebase: Error (auth/visibility-check-was-unavailable)',
  // Firebase's IndexedDBLocalPersistence polls its object store every 800ms from a bare
  // `setInterval(async () => this._poll())`, so nothing ever observes the promise it returns.
  // WebKit closes IndexedDB connections when it suspends a backgrounded page, and `transaction()`
  // on a closing connection throws synchronously, which outlives the four internal reopen retries
  // while the page stays suspended. The rejection therefore escapes as an unhandled rejection.
  // The next poll after the page resumes reopens the database and auth state is unaffected, and
  // the whole path is inside firebase/auth, so there is nothing of ours to fix. Scoped to
  // `transaction()` specifically: a write that fails against our own stores still reports.
  /Failed to execute 'transaction' on 'IDBDatabase': The database connection is closing/i,
  // WebKit's IndexedDB server process dies (typically while the page is backgrounded) and every
  // later IDB call on the page fails with this until it is reloaded. Firebase auth hits it when it
  // persists a refreshed token: getIdToken() stores the new token in memory first, then rethrows the
  // failed write as auth/internal-error, so whichever of our token callers catches it logs it. The
  // session keeps working from memory and the next load restores persistence; an uncaught one
  // already reloads via the unhandledrejection handler in index.tsx. Also covers the stackless
  // `UnknownError:` form our own idb-store raises (MARKET-1QT), which idb-store already swallows.
  /Connection to Indexed Database server lost/i,
  // WebKit force-closes every open IndexedDB connection with this when the user clears website
  // data (Safari settings, or ITP's storage purge) while the page is open (MARKET-2M4). The stores
  // are gone, so the session is already lost and the next load starts signed out; nothing retries.
  /Database deleted by request of the user/i,
  // Chromium's form of the same failed token write: the IndexedDB transaction Firebase auth uses to
  // persist the refreshed user is aborted under it (MARKET-2M2, Edge). The new token is already in
  // memory, so the session is unaffected. Scoped to Firebase's auth/internal-error wrapper so an
  // aborted transaction against our own stores still reports.
  /AuthError has occurred: The transaction was aborted, so the request cannot be fulfilled/i,

  // Permissions & Browser Restrictions
  /UnknownError: The user denied permission to access the database/i,
  // The device's storage is full, so Chrome cannot open the IndexedDB backing store at all. Every
  // IDB path already degrades gracefully (idb-store swallows and falls back, FCM clears its token),
  // and no retry helps until the user frees space. A genuine per-origin quota overflow is a
  // different error - it fails on a write, with different wording - and keeps reporting, because
  // that one is our own storage footprint to fix.
  /Encountered full disk while opening backing store/i,
  /Messaging: The notification permission was not granted/i,
  /NotAllowedError: Registration failed - permission denied/i,

  // Business Logic / Custom Auth State
  'Your profile is not active, contact support for information',
  'Only Nile users can sign up via the Google or Apple provider',

  // Auth & Session State
  /Signout (error|failed)/i,
  'Unauthorized request',

  // Expected auth failure: user lacks permission; BE already logs
  /not authorized to view source document/i,

  // Combined errors
  /CombinedError: \[Network\] Attempted to assign to readonly property/i,

  // graphql-ws leaks the raw WebSocket event as an orphaned promise rejection when a
  // subscription is torn down while its connection is still being established and the
  // socket dies before the connect settles: subscribe()'s `if (done) return release()`
  // drops the Promise.race that would have observed it. The socket's `close` gives a
  // CloseEvent, its `error` a bare Event that by spec carries no detail at all. Either
  // way the subscription is already gone and the client reconnects on its own, so there
  // is nothing to act on.
  /Event `CloseEvent` \(type=close\) captured as promise rejection/i,
  /Event `Event` \(type=error\) captured as promise rejection/i,
];

const UUID_REGEX = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;
const LONG_NUMBER_REGEX = /\b\d{5,}\b/g;
const GENERIC_MESSAGES = new Set(['', 'error', 'undefined', 'null']);

/**
 * Normalize message for fingerprinting: strip UUIDs and long IDs to avoid
 * issue explosion and PII in fingerprints.
 */
function normalizeFingerprintMessage(message: string): string {
  return message.replace(UUID_REGEX, '<uuid>').replace(LONG_NUMBER_REGEX, '<id>').trim();
}

export type SentryEventForFingerprint = {
  exception?: { values?: Array<{ type?: string; value?: string }> };
  message?: string;
};

/**
 * Fingerprint by error type + normalized message to align grouping with backend.
 * Returns ['{{ default }}'] when message is empty/generic so Sentry uses default grouping.
 * https://docs.sentry.io/concepts/data-management/event-grouping/fingerprint-rules/
 * https://docs.sentry.io/platforms/javascript/enriching-events/fingerprinting/
 */
export function getSentryFingerprint(event: SentryEventForFingerprint): string[] {
  const type = event.exception?.values?.[0]?.type ?? 'Error';
  const rawMessage = event.message ?? event.exception?.values?.[0]?.value ?? '';
  const message = normalizeFingerprintMessage(String(rawMessage));

  if (GENERIC_MESSAGES.has(message.toLowerCase())) {
    return ['{{ default }}'];
  }
  return [type, message];
}

export function getSentryInitOptions({ dsn }: { dsn: string }): SentryInitOptions {
  const appBuild = getNileAppBuild();
  return {
    beforeSend(event: SentryBeforeSendEvent) {
      event.fingerprint = getSentryFingerprint(event);
      return event;
    },
    dsn,
    environment: isProd ? 'production' : 'staging',
    ignoreErrors: SENTRY_IGNORE_ERRORS,
    // Matches the build in the iOS app's own release, ag.nile.market@<version>+<build>.
    initialScope: appBuild ? { tags: { 'app.build': appBuild } } : undefined,
    integrations: [browserTracingIntegration(), browserProfilingIntegration()],
    maxBreadcrumbs: 50,
    // Set at build time by the deploy; a local build has none.
    release: process.env.SENTRY_RELEASE,
    tracesSampleRate: isProd ? 1 : 0.5,
  };
}
